/** Basic game dynamics - i.e., choose the next action / challenge based on the current one, the player's state, and the game context */

import type { GameResult, Persisted, UserLevel, UserStats } from '@tunasong/schemas'

/** Map of score thresholds to user levels */
export const guitarGameLevels = {
  dylan: {
    proficiency: 1,
    name: 'Bob Dylan',
    description: 'Anything goes',
    minScore: 0,
    maxScore: 299,
  },
  cobain: {
    proficiency: 2,
    name: 'Kurt Cobain',
    description: 'Pour some chords on me',
    minScore: 300,
    maxScore: 699,
  },
  swift: {
    proficiency: 3,
    name: 'Taylor Swift',
    description: 'Strumming your way to the top',
    minScore: 700,
    maxScore: 1499,
  },
  clapton: {
    proficiency: 4,
    name: 'Eric Clapton',
    description: 'Strange brew shining sunshine on your riffs',
    minScore: 1500,
    maxScore: 2999,
  },
  page: {
    name: 'Jimmy Page',
    proficiency: 5,
    description: 'Whole lotta riffs',
    minScore: 3000,
    maxScore: 4999,
  },
  gilmour: {
    proficiency: 6,
    name: 'David Gilmour',
    description: 'Mastering the game and the feel of the guitar',
    minScore: 5000,
    maxScore: 9999,
  },
  hendrix: {
    proficiency: 7,
    name: 'Jimi Hendrix',
    description: 'Riffs and licks that will make you cry',
    minScore: 10000,
    maxScore: Infinity,
  },
} satisfies Record<string, UserLevel>

export const getLevel = (score: number): UserLevel => {
  const levels = Object.entries(guitarGameLevels)
    .filter(([, level]) => score >= level.minScore)
    .map(([, level]) => level)

  return levels[levels.length - 1] ?? guitarGameLevels.dylan
}

export const getStreak = (gameResults: GameResult[], endDateIsoString?: string): number => {
  // A streak is defined as the number of unique consecutive days with one or more game results, ending today or yesterday.
  if (!gameResults.length) {
    return 0
  }

  // Get unique days in descending order (most recent first)
  const uniqueDays = Array.from(new Set(gameResults.map(result => new Date(result.createdAt).toDateString())))
    .map(dateStr => new Date(dateStr))
    .sort((a, b) => b.getTime() - a.getTime())

  // Start streak only if the most recent day is today or yesterday
  const today = endDateIsoString ? new Date(endDateIsoString) : new Date()
  today.setHours(0, 0, 0, 0)
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)

  if (
    uniqueDays.length === 0 ||
    (uniqueDays[0].getTime() !== today.getTime() && uniqueDays[0].getTime() !== yesterday.getTime())
  ) {
    return 0
  }

  let streak = 1
  let lastDate = uniqueDays[0]

  for (let i = 1; i < uniqueDays.length; i++) {
    const expected = new Date(lastDate)
    expected.setDate(expected.getDate() - 1)
    if (uniqueDays[i].getTime() === expected.getTime()) {
      streak++
      lastDate = uniqueDays[i]
    } else {
      break
    }
  }

  return streak
}

export const getUserStats = (userGameResults: GameResult[]) => {
  const scoreSum = userGameResults.reduce((sum, result) => sum + (result.score ?? 0), 0)
  const scoreHigh = Math.max(...userGameResults.map(result => result.score ?? 0), 0)
  const lastPlayed = Math.max(...userGameResults.map(result => new Date(result.createdAt).getTime()), 0)

  return {
    xp: scoreSum,
    level: getLevel(scoreSum),
    streak: getStreak(userGameResults),
    scoreHigh,
    lastPlayed: new Date(lastPlayed).toISOString(),
  } satisfies UserStats
}

export const getHighScore = (gameResults: Persisted<GameResult>[]) => {
  let high: GameResult | null = null
  for (const result of gameResults) {
    if ((result.score ?? 0) > (high?.score ?? 0)) {
      high = result
    }
  }
  return high
}

export const getLeaderBoard = (gameResults: Persisted<GameResult>[], leaderBoardSize = 10) => {
  const userHighScores = new Map<string, Persisted<GameResult>>()

  for (const result of gameResults) {
    if (!result.userId) {
      continue
    }
    const userHighScore = userHighScores.get(result.userId)?.score ?? 0
    if (userHighScore < (result.score ?? 0)) {
      userHighScores.set(result.userId, result)
    }
  }

  // Pick the top leaderBoardSize scores
  const leaderBoard = Array.from(userHighScores.values())
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
    .slice(0, leaderBoardSize)

  return leaderBoard
}
